import { useState } from "react";
import { Download, FileSpreadsheet, FileText, FileType } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PeriodPicker } from "@/components/PeriodPicker";
import { presetRange, type DateRange, type Preset } from "@/lib/periods";
import { downloadBlob, fetchBlob } from "@/lib/api";

export function ReportsPage() {
  const [preset, setPreset] = useState<Preset>("last30");
  const [custom, setCustom] = useState<DateRange>(presetRange("last30"));
  const range = preset === "custom" ? custom : presetRange(preset);

  const today = new Date();
  const [month, setMonth] = useState(
    `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`,
  );

  return (
    <div className="space-y-4 p-6">
      <header>
        <h1 className="text-xl font-semibold">내보내기</h1>
        <p className="text-xs text-muted-foreground">
          CSV / Excel / 월간 PDF 다운로드
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>다운로드 기간</CardTitle>
        </CardHeader>
        <CardContent>
          <PeriodPicker
            preset={preset}
            custom={custom}
            onChange={(p, c) => {
              setPreset(p);
              setCustom(c);
            }}
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <DownloadCard
          icon={<FileText className="h-5 w-5 text-primary" />}
          title="원본 로그 CSV"
          desc="기간 내 모든 질의 로그 (피드백 컬럼 포함)"
          onClick={() => downloadCsv(range)}
          buttonLabel="CSV 다운로드"
        />
        <DownloadCard
          icon={<FileSpreadsheet className="h-5 w-5 text-green-600" />}
          title="Excel 보고서"
          desc="요약 / 일별 / 사용자 TOP 시트, 차트 포함"
          onClick={() => downloadExcel(range)}
          buttonLabel="Excel 다운로드"
        />
        <DownloadCard
          icon={<FileType className="h-5 w-5 text-red-600" />}
          title="월간 PDF 보고서"
          desc="고정 7페이지 — 표지·요약·일별·히트맵·TOP·피드백·미답변"
          extra={
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-40"
            />
          }
          onClick={() => downloadPdf(month)}
          buttonLabel="PDF 다운로드"
        />
      </div>
    </div>
  );
}

function DownloadCard(props: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  extra?: React.ReactNode;
  onClick: () => Promise<void> | void;
  buttonLabel: string;
}) {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    setBusy(true);
    try {
      await props.onClick();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">{props.icon}<CardTitle>{props.title}</CardTitle></div>
        <CardDescription>{props.desc}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {props.extra}
        <Button onClick={handle} disabled={busy} className="gap-2 w-full">
          <Download className="h-4 w-4" />
          {busy ? "다운로드 중…" : props.buttonLabel}
        </Button>
      </CardContent>
    </Card>
  );
}

async function downloadCsv(range: DateRange) {
  const blob = await fetchBlob("/export/csv", { from: range.from, to: range.to });
  const fname = `arkiveai_logs_${range.from.slice(0, 10)}_to_${range.to.slice(0, 10)}.csv`;
  downloadBlob(blob, fname);
}
async function downloadExcel(range: DateRange) {
  const blob = await fetchBlob("/reports/excel", { from: range.from, to: range.to });
  const fname = `arkiveai_report_${range.from.slice(0, 10)}_to_${range.to.slice(0, 10)}.xlsx`;
  downloadBlob(blob, fname);
}
async function downloadPdf(month: string) {
  const blob = await fetchBlob("/reports/monthly", { month });
  downloadBlob(blob, `arkiveai_monthly_${month}.pdf`);
}
